import { match } from "ts-pattern";

import { IncidentWorkItemStatusSchema } from "@thalamos/common";

import { AvailableLanguagesSchema } from "../../types/availableLanguages.js";
import { FormSectionSchema } from "../../types/FormSection.js";
import { IncidentFormTypeSchema } from "../../types/incidentWorkItem.js";
import { type RequestPolicePresenceMinimumFields } from "../../types/workflow/index.js";
import type { ActionInput } from "../ActionInput.js";
import { calculateIncidentWorkItemStatus } from "../calculateIncidentWorkItemStatus.js";
import {
  hasSharedForm,
  incidentIsActive,
  mcaFormCompleted,
  userIsIncidentOwner,
} from "../helpers.js";
import { getSupportedIncidentTypeSelection } from "../helpers/getIncidentTypeSelection.js";

export type EndIncidentAction = {
  canEnd: boolean;
  endIncidentType:
    | "preTriageEndIncident"
    | typeof IncidentFormTypeSchema.enum.preEndIncident
    | typeof IncidentFormTypeSchema.enum.finishForm;
};

function canEndIncident(actionInput: ActionInput): boolean {
  const incidentStatusIsUnknown =
    calculateIncidentWorkItemStatus(actionInput.incidentWorkItem).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum.unknown;

  return (
    (incidentIsActive(actionInput) || incidentStatusIsUnknown) &&
    userIsIncidentOwner(actionInput)
  );
}

function getEndIncidentType(
  actionInput: ActionInput,
): EndIncidentAction["endIncidentType"] {
  const incidentStatusIsUnknown =
    calculateIncidentWorkItemStatus(actionInput.incidentWorkItem).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum.unknown;

  if (incidentStatusIsUnknown) {
    return "preTriageEndIncident";
  }

  // A complete MCA form is the end of that journey, so the incident is ready to
  // finish. This guard also requires the triage answer to say MCA, so that a
  // section 136 incident carrying a complete MCA form is not routed here.
  const isMcaIncident =
    getSupportedIncidentTypeSelection(actionInput).unwrapOr(undefined) ===
    "mca";

  if (isMcaIncident && mcaFormCompleted(actionInput)) {
    return IncidentFormTypeSchema.enum.finishForm;
  }

  // Keep this check for now until all organisations have been migrated
  const language = actionInput.user.sessionOrganisationConfiguration?.language;
  if (
    (
      [
        AvailableLanguagesSchema.enum.MetropolitanPolice,
        AvailableLanguagesSchema.enum.CityOfLondonPolice,
      ] as unknown as Array<typeof language>
    ).includes(language)
  ) {
    const sharedFormExists = hasSharedForm(actionInput);
    const isMcaComplete = mcaFormCompleted(actionInput);

    return sharedFormExists || isMcaComplete
      ? IncidentFormTypeSchema.enum.finishForm
      : IncidentFormTypeSchema.enum.preEndIncident;
  }

  const requestPolicePresenceForms = actionInput.incidentWorkItem.forms.filter(
    (form) =>
      form.formType === IncidentFormTypeSchema.enum.requestPolicePresence,
  );

  const positiveEngagementForms = requestPolicePresenceForms.filter((form) => {
    const requestPolicePresenceFormSection = form.sections.find(
      (section) =>
        section.id === FormSectionSchema.enum.requestPolicePresenceMain,
    );
    const data =
      requestPolicePresenceFormSection?.data as RequestPolicePresenceMinimumFields;

    return ["no", "yes"].includes(data.hasPolicePresenceRequested.value);
  });

  // If 'handover' has been started through positive nhs engagement
  // then we allow the user to finish the form,
  // otherwise we show the pre-end incident form
  if (positiveEngagementForms.length === 0)
    return IncidentFormTypeSchema.enum.preEndIncident;

  return IncidentFormTypeSchema.enum.finishForm;
}

export function calculateEndIncidentActions(
  actionInput: ActionInput,
): EndIncidentAction {
  return {
    canEnd: canEndIncident(actionInput),
    endIncidentType: getEndIncidentType(actionInput),
  };
}

export type EndIncidentDestination =
  "finishForm" | "preEndIncident" | "preTriageDialog" | "cannotEnd";

/**
 * Where ending the incident takes the officer. Route to an ending through this
 * rather than by reading `endIncidentType`, which still names a form once the
 * incident can no longer be ended, and so that a new end type is a compile
 * error here instead of a silent misroute at each caller.
 */
export function endIncidentDestination({
  canEnd,
  endIncidentType,
}: EndIncidentAction): EndIncidentDestination {
  if (!canEnd) {
    return "cannotEnd";
  }

  return match(endIncidentType)
    .returnType<EndIncidentDestination>()
    .with("preTriageEndIncident", () => "preTriageDialog")
    .with(IncidentFormTypeSchema.enum.preEndIncident, () => "preEndIncident")
    .with(IncidentFormTypeSchema.enum.finishForm, () => "finishForm")
    .exhaustive();
}
